/**
 * Screen categorization utility for managing screen-to-category mapping
 * and category navigation logic in the alumni survey application.
 */

// Screen categories configuration
export const SCREEN_CATEGORIES = [
  {
    id: 'preliminary',
    name: 'preliminary',
    displayName: 'Getting Started',
    isOptional: false,
    screens: [0, 1, 2], // SplashPage, PrivacyPolicy, VerificationForm
  },
  {
    id: 'required',
    name: 'required',
    displayName: 'Required Information',
    isOptional: false,
    screens: [3, 4, 5, 6], // CivilStatus, Address, Contact, EmploymentStatus
  },
  {
    id: 'optional',
    name: 'optional',
    displayName: 'Additional Information',
    isOptional: true,
    screens: [7, 8, 9, 10, 11], // Transition, Examinations, TwoYearStatus, Employment, CollegeQuestions
  },
  {
    id: 'completion',
    name: 'completion',
    displayName: 'Completion',
    isOptional: false,
    screens: [12], // Epilogue
  },
];

/**
 * ScreenCategorizer utility class for managing screen categorization
 */
export class ScreenCategorizer {
  constructor(categories = SCREEN_CATEGORIES) {
    this.categories = categories;
    this._screenToCategory = this._buildScreenToCategoryMap();
  }

  /**
   * Build a map from screen index to category for quick lookups
   * @private
   */
  _buildScreenToCategoryMap() {
    const map = new Map();
    this.categories.forEach((category) => {
      category.screens.forEach((screenIndex) => {
        map.set(screenIndex, category);
      });
    });
    return map;
  }

  /**
   * Get the category for a given screen index
   * @param {number} screenIndex - The screen index
   * @returns {Object|null} The category object or null if not found
   */
  getCategoryForScreen(screenIndex) {
    return this._screenToCategory.get(screenIndex) || null;
  }

  /**
   * Get all categories
   * @returns {Array} Array of category objects
   */
  getCategories() {
    return [...this.categories];
  }

  /**
   * Get category by ID
   * @param {string} categoryId - The category ID
   * @returns {Object|null} The category object or null if not found
   */
  getCategoryById(categoryId) {
    return this.categories.find((cat) => cat.id === categoryId) || null;
  }

  /**
   * Get the index of a category
   * @param {string} categoryId - The category ID
   * @returns {number} The category index or -1 if not found
   */
  getCategoryIndex(categoryId) {
    return this.categories.findIndex((cat) => cat.id === categoryId);
  }

  /**
   * Check if a category is completed based on current screen
   * @param {string} categoryId - The category ID
   * @param {number} currentScreenIndex - The current screen index
   * @returns {boolean} True if category is completed
   */
  isCategoryCompleted(categoryId, currentScreenIndex) {
    const category = this.getCategoryById(categoryId);
    if (!category) return false;

    const maxScreenInCategory = Math.max(...category.screens);
    return currentScreenIndex > maxScreenInCategory;
  }

  /**
   * Get the current category based on screen index
   * @param {number} screenIndex - The screen index
   * @returns {Object|null} The current category object
   */
  getCurrentCategory(screenIndex) {
    return this.getCategoryForScreen(screenIndex);
  }

  /**
   * Get the next category after the given category
   * @param {string} categoryId - The current category ID
   * @returns {Object|null} The next category object or null if none
   */
  getNextCategory(categoryId) {
    const currentIndex = this.getCategoryIndex(categoryId);
    if (currentIndex === -1 || currentIndex >= this.categories.length - 1) {
      return null;
    }
    return this.categories[currentIndex + 1];
  }

  /**
   * Get the previous category before the given category
   * @param {string} categoryId - The current category ID
   * @returns {Object|null} The previous category object or null if none
   */
  getPreviousCategory(categoryId) {
    const currentIndex = this.getCategoryIndex(categoryId);
    if (currentIndex <= 0) {
      return null;
    }
    return this.categories[currentIndex - 1];
  }

  /**
   * Get the first screen index of a category
   * @param {string} categoryId - The category ID
   * @returns {number|null} The first screen index or null if category not found
   */
  getFirstScreenOfCategory(categoryId) {
    const category = this.getCategoryById(categoryId);
    if (!category || category.screens.length === 0) return null;
    return Math.min(...category.screens);
  }

  /**
   * Get the last screen index of a category
   * @param {string} categoryId - The category ID
   * @returns {number|null} The last screen index or null if category not found
   */
  getLastScreenOfCategory(categoryId) {
    const category = this.getCategoryById(categoryId);
    if (!category || category.screens.length === 0) return null;
    return Math.max(...category.screens);
  }

  /**
   * Check if we should show transition screen after completing a category
   * @param {number} currentScreenIndex - The current screen index
   * @returns {boolean} True if transition should be shown
   */
  shouldShowTransition(currentScreenIndex) {
    const currentCategory = this.getCurrentCategory(currentScreenIndex);
    if (!currentCategory) return false;

    // Show transition after completing required category
    if (currentCategory.id === 'required') {
      const lastRequiredScreen = this.getLastScreenOfCategory('required');
      return currentScreenIndex === lastRequiredScreen;
    }

    return false;
  }

  /**
   * Get progress within a category
   * @param {string} categoryId - The category ID
   * @param {number} currentScreenIndex - The current screen index
   * @returns {Object} Progress object with current, total, and percentage
   */
  getCategoryProgress(categoryId, currentScreenIndex) {
    const category = this.getCategoryById(categoryId);
    if (!category) {
      return { current: 0, total: 0, percentage: 0 };
    }

    const screensInCategory = category.screens.sort((a, b) => a - b);
    const currentScreenInCategory = screensInCategory.findIndex(
      (screen) => screen === currentScreenIndex
    );

    const current = currentScreenInCategory >= 0 ? currentScreenInCategory + 1 : 0;
    const total = screensInCategory.length;
    const percentage = total > 0 ? (current / total) * 100 : 0;

    return { current, total, percentage };
  }

  /**
   * Validate screen categories configuration
   * @returns {Array} Array of validation errors, empty if valid
   */
  validateConfiguration() {
    const errors = [];
    const allScreens = new Set();

    this.categories.forEach((category, index) => {
      // Check required fields
      if (!category.id) {
        errors.push(`Category at index ${index} missing required 'id' field`);
      }
      if (!category.name) {
        errors.push(`Category at index ${index} missing required 'name' field`);
      }
      if (!category.displayName) {
        errors.push(`Category at index ${index} missing required 'displayName' field`);
      }
      if (typeof category.isOptional !== 'boolean') {
        errors.push(`Category at index ${index} 'isOptional' must be boolean`);
      }
      if (!Array.isArray(category.screens)) {
        errors.push(`Category at index ${index} 'screens' must be an array`);
      }

      // Check for duplicate screen assignments
      if (Array.isArray(category.screens)) {
        category.screens.forEach((screenIndex) => {
          if (allScreens.has(screenIndex)) {
            errors.push(`Screen ${screenIndex} assigned to multiple categories`);
          }
          allScreens.add(screenIndex);
        });
      }
    });

    return errors;
  }
}

// Export a default instance
export default new ScreenCategorizer();
